import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { api, edenFetch } from "@/lib/eden";
import { trackFunnel, BIRTHDAY_LIST_SURFACE, type FunnelEventName } from "@/lib/funnel-events";
import { queryClient } from "@/lib/queryClient";
import { invalidateContactData } from "@/site/components/contacts/contact-data";

/**
 * Shared birthday lists + linked accounts (PRD 16) — the web data contracts. Every user route
 * here is FULL-session (D4): a soft email-link session gets 401, which eden.ts turns into a
 * sign-out and the root guard into `/login?next=` (only the composer's signer candidates
 * accept soft). A list or link that isn't the caller's answers 404, never 403.
 *
 * Wire shapes are hand-typed (the list detail route has no response schema), the same
 * convention as br-data.ts / sign-data.ts.
 */

export const LIST_KINDS = ["family", "friends", "work", "club", "other"] as const;
export type ListKind = (typeof LIST_KINDS)[number];

/** D8 — the one-tap kind chips. "club" reads "Club or class" (the mock-up's wording). */
export const KIND_LABEL: Record<ListKind, string> = {
  family: "Family",
  friends: "Friends",
  work: "Work",
  club: "Club or class",
  other: "Other",
};

/** "Who's missing?" wording per kind — work says colleagues (§6.1 "Defaults by kind"). */
export const KIND_MISSING_COPY: Record<ListKind, string> = {
  family: "Send this list to anyone in the family who isn't on it yet.",
  friends: "Send this list to any friends who aren't on it yet.",
  work: "Send this list to any colleagues who aren't on it yet.",
  club: "Send this list to anyone in the group who isn't on it yet.",
  other: "Send this list to anyone who isn't on it yet.",
};

export function isListKind(v: unknown): v is ListKind {
  return typeof v === "string" && (LIST_KINDS as readonly string[]).includes(v);
}

/** The kind a server row carries, narrowed (an unknown code reads as "other"). */
export function kindOf(code: string | null | undefined): ListKind {
  return isListKind(code) ? code : "other";
}

/**
 * The name the kind fills in (D8 "The kind fills in a suggested name"). Family takes the
 * member's surname when the account name has one ("Smith family"); the rest are plain nouns
 * that read well in "<name> birthdays" on the join page.
 */
export function suggestedListName(kind: ListKind, displayName: string | null | undefined): string {
  const parts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
  const surname = parts.length > 1 ? parts[parts.length - 1] : null;
  switch (kind) {
    case "family":
      return surname ? `${surname} family` : "Our family";
    case "friends":
      return "Friends";
    case "work":
      return "Work";
    case "club":
      return "Our club";
    default:
      return "Our group";
  }
}

// ── Wire shapes ─────────────────────────────────────────────────────────────────────────────

/** GET /api/public/lists/:token — the join preview. Never dates, surnames or addresses. */
export interface ListPreview {
  name: string;
  kind_code: string;
  owner_first_name: string | null;
  member_first_names: string[];
  member_count: number;
  is_full: boolean;
  /** The FULL-session viewer's own live membership, if any. */
  viewer_status: "pending" | "active" | null;
  /** Only for a viewer already on the list (opens the list page). */
  birthday_list_id: string | null;
}

/** One row of GET /api/user/lists. */
export interface MyList {
  birthday_list_id: string;
  name: string;
  kind_code: string;
  role: "owner" | "member";
  status: "pending" | "active";
  member_count: number;
  token: string;
  share_url: string;
  created_at: string;
}

export interface MyListsPayload {
  lists: MyList[];
}

export interface ListMemberBirthday {
  month: number;
  day: number;
  /** Null when they hide it (D9). */
  year: number | null;
  /** The next date in the VIEWER's own timezone (server withEffectiveNextOccurrence). */
  next_occurrence_on: string | null;
}

export interface ListMember {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  is_you: boolean;
  role: "owner" | "member";
  status: "pending" | "active";
  birthday: ListMemberBirthday | null;
}

export interface ListUpdate {
  kind: "joined" | "date_changed" | "name_changed";
  user_id: string;
  first_name: string | null;
  at: string;
}

/** GET /api/user/lists/:id — the list page. */
export interface ListDetail {
  birthday_list_id: string;
  name: string;
  kind_code: string;
  token: string;
  share_url: string;
  is_owner: boolean;
  members: ListMember[];
  updates: ListUpdate[];
}

/** POST /api/user/lists/join/:token. */
export interface JoinResult {
  birthday_list_id: string;
  /** pending = joined, waiting for the email to be confirmed (D3). */
  status: "pending" | "active" | "already_member";
}

/** One row of GET /api/user/links — an active link. */
export interface MemberLink {
  member_link_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  linked_at: string | null;
  source_code: string;
  /** The lists this person is also on with me. */
  lists: { birthday_list_id: string; name: string }[];
}

/** One row of GET /api/user/links/requests — a request waiting on me. */
export interface LinkRequest {
  member_link_id: string;
  requester_user_id: string;
  requester_first_name: string | null;
  requested_at: string;
  source_code: string;
  /** What accepting would give ME (the requester's side). */
  shares: {
    has_birthday: boolean;
    is_year_shared: boolean;
    is_anniversary_shared: boolean;
    dependants_count: number;
  };
}

export interface ShareSettings {
  is_anniversary_shared: boolean;
  is_dependants_shared: boolean;
}

/** The slice of GET /user/me these pages read (same endpoint + cache as account settings). */
export interface MeForLists {
  display_name: string | null;
  self_contact_id: string;
  keep_age_secret: boolean;
  birthday: {
    reminder_id: string;
    event_month: number | null;
    event_day: number | null;
    event_year: number | null;
  } | null;
  anniversary: { event_month: number | null; event_day: number | null } | null;
}

// ── Keys + fetchers ─────────────────────────────────────────────────────────────────────────

export const listKeys = {
  all: ["/api/user/lists"] as const,
  detail: (id: string) => ["/api/user/lists", id] as const,
  preview: (token: string) => ["/api/public/lists", token] as const,
  candidates: (listId: string, cardSendId: string) =>
    ["/api/user/lists", listId, "signer-candidates", cardSendId] as const,
};

export const linkKeys = {
  all: ["/api/user/links"] as const,
  requests: ["/api/user/links/requests"] as const,
  shareSettings: ["/api/user/share-settings"] as const,
};

export const fetchMyLists = () => edenFetch(api.api.user.lists.get()) as Promise<MyListsPayload>;

export const fetchListDetail = (id: string) =>
  edenFetch(api.api.user.lists({ id }).get()) as Promise<ListDetail>;

export const fetchListPreview = (token: string) =>
  edenFetch(api.api.public.lists({ token }).get()) as Promise<ListPreview>;

export const joinList = (token: string) =>
  edenFetch(api.api.user.lists.join({ token }).post()) as Promise<JoinResult>;

export const fetchLinks = () =>
  edenFetch(api.api.user.links.get()) as Promise<{ links: MemberLink[] }>;

export const fetchLinkRequests = () =>
  edenFetch(api.api.user.links.requests.get()) as Promise<{ requests: LinkRequest[] }>;

export const fetchShareSettings = () =>
  edenFetch(api.api.user["share-settings"].get()) as Promise<ShareSettings>;

/** Where a link ask came from — the `source_code` the server records and the funnel detail. */
export type LinkAskSource =
  | { contact_id: string }
  | { card_send_id: string }
  | { birthday_request_id: string };

/** POST /api/user/links. `active` = they had already asked me, so we're linked now. */
export const requestLink = (from: LinkAskSource) =>
  edenFetch(api.api.user.links.post(from)) as Promise<{
    member_link_id: string;
    status: "requested" | "active";
  }>;

/** Refresh everything a join / leave / link change moves: the lists, the links, and the
 *  synced contacts + reminders the sync writes into the member's own account. */
export function invalidateListData(): void {
  void queryClient.invalidateQueries({ queryKey: listKeys.all });
  void queryClient.invalidateQueries({ queryKey: linkKeys.all });
  void queryClient.invalidateQueries({ queryKey: linkKeys.requests });
  void queryClient.invalidateQueries({ queryKey: ["/api/public/lists"] });
  invalidateContactData();
}

// ── Funnel (PRD 16 §9) ──────────────────────────────────────────────────────────────────────

/** A list funnel beacon: surface `birthday_list`, detail = the list's kind (server allowlist). */
export function trackList(
  event: Extract<
    FunnelEventName,
    | "list_start_viewed"
    | "list_created"
    | "list_shared"
    | "list_join_viewed"
    | "list_join_submitted"
    | "list_joined"
    | "list_shared_again"
  >,
  kind: string | null | undefined,
): void {
  trackFunnel(event, { surface: BIRTHDAY_LIST_SURFACE, detail: kind ?? null });
}

/** A link funnel beacon: detail = where the ask came from (contact | card | birthday_request). */
export function trackLink(event: "link_requested" | "link_accepted", source: string): void {
  trackFunnel(event, { surface: BIRTHDAY_LIST_SURFACE, detail: source });
}

// ── Display helpers ─────────────────────────────────────────────────────────────────────────

export function fullName(p: { first_name: string | null; last_name: string | null }): string {
  return [p.first_name, p.last_name].filter(Boolean).join(" ");
}

/** "March 14" — month and day only (the year is a separate, hideable fact). */
export function monthDay(month: number, day: number): string {
  return format(new Date(2000, month - 1, day), "MMMM d");
}

/** Whole days from today (the member's local calendar) to a `YYYY-MM-DD` date. */
export function daysUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const d = parseISO(isoDate.slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  return differenceInCalendarDays(d, new Date());
}

/** "today" / "tomorrow" / "in 28 days". */
export function inDaysLabel(days: number | null): string | null {
  if (days == null || days < 0) return null;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/** The next month/day occurrence from today (for the member's own birthday from /user/me). */
export function nextOccurrenceOf(month: number, day: number): string {
  const today = new Date();
  const thisYear = today.getFullYear();
  const candidate = (y: number) => {
    // Feb 29 in a common year falls on Mar 1, the server's rule for the shown date.
    const d = new Date(y, month - 1, day);
    return d.getMonth() === month - 1 ? d : new Date(y, 2, 1);
  };
  let next = candidate(thisYear);
  if (differenceInCalendarDays(next, today) < 0) next = candidate(thisYear + 1);
  return format(next, "yyyy-MM-dd");
}

/** "turns 40" when the year is shared and the next date is known (D9's milestone). */
export function turnsLabel(b: ListMemberBirthday): string | null {
  if (!b.year || !b.next_occurrence_on) return null;
  const age = Number(b.next_occurrence_on.slice(0, 4)) - b.year;
  return age > 0 && age < 130 ? `turns ${age}` : null;
}
